import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { MentionConfig, Mentions } from 'angular-mentions';
import { Store } from '@ngxs/store';
import { FieldDef } from '@core/models';
import { UserState } from '@state/user/state';
import { TeamsService } from '@modules/settings/teams/services/teams.service';

declare interface Mention {
  full_name: string;
  id?: number;
  isTeam?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MentionsService {
  private readonly store = inject(Store);
  private readonly teamsService = inject(TeamsService);

  private mentionMappings: { [userFriendlyMention: string]: string } = {};
  private teams = toSignal(this.teamsService.getAllTeams(), { initialValue: [] });
  private users = toSignal(this.store.select(UserState.getActiveUsers), { initialValue: [] });

  /**
   * Get the mentions configuration for fields that use functions and field calcs.
   */
  public getFieldMentionConfig(): MentionConfig {
    return {
      mentions: [
        {
          items: ['SUM', 'MULTIPLY', 'ADD', 'AVG', 'MED', 'ROUND', 'DIVIDE', 'SUBTRACT'],
          triggerChar: '@',
          labelKey: 'name',
          returnTrigger: true,
          allowSpace: true,
          maxItems: 4,
          mentionSelect: this.fieldMentionSelect.bind(this),
        },
        {
          items: [],
          triggerChar: '#',
          labelKey: 'label',
          returnTrigger: true,
          allowSpace: true,
          maxItems: 4,
          mentionSelect: this.fieldMentionSelect.bind(this),
        },
      ],
    };
  }

  /**
   * Get the mentions configuration for comments.
   */
  getDefaultMentionConfig(extraMentions?: Mentions[]): MentionConfig {
    return {
      mentions: [
        {
          items: [
            ...this.users()
              .filter((user) => user && user.full_name !== null)
              .map((user) => ({ full_name: user.full_name, id: user.id, isTeam: false })),
            { full_name: 'Everyone' },
            ...this.teams().map((team) => ({ full_name: team.name, id: team.id, isTeam: true })),
          ] as Mention[],
          triggerChar: '@',
          labelKey: 'full_name',
          allowSpace: true,
          maxItems: 10,
          mentionSelect: this.teamAndUserMentionSelect.bind(this),
        },
        ...(extraMentions || []),
      ],
    };
  }

  private teamAndUserMentionSelect(value: Mention): string {
    /* eslint-disable @typescript-eslint/naming-convention */
    const { full_name, id, isTeam } = value;
    const userFriendlyMention = `@${full_name.replace(/\s/g, '')}`;

    // Logic to determine if backend mention should include concatenated ID
    if (full_name === 'Everyone') {
      // Special case: "everyone".
      this.mentionMappings[userFriendlyMention] = userFriendlyMention; // No concatenated ID
      return userFriendlyMention;
    }

    // Default case: concatenate ID for backend with new format
    const backendFriendlyMention = `@${isTeam ? 'team' : 'user'}_${userFriendlyMention.replace('@', '')}_${id}`;

    this.mentionMappings[userFriendlyMention] = backendFriendlyMention; // Store in mapping
    return userFriendlyMention;
  }

  defaultHashtagMentionSelect(value: any): string {
    const name = value.full_name;
    const userFriendlyMention = `#${name.replace(/\s/g, '')}`;

    // Default case: concatenate ID for backend
    const backendFriendlyMention = `#${userFriendlyMention}_${value.id}`;
    this.mentionMappings[userFriendlyMention] = backendFriendlyMention; // Store in mapping
    return userFriendlyMention;
  }

  /**
   * Handle the mention selection for field calcs.
   */
  private fieldMentionSelect(value: FieldDef): string {
    return value.meta ? value.name : value.name + '(  )';
  }

  /**
   * Transform mentions in text to backend format
   */
  transformMentionsForBackend(body: string): string {
    if (!body) {
      return '';
    }

    // Replace user-friendly mentions with backend mentions for both @ and #
    let transformedBody = body.replace(/[@#](\w+)/g, (match: string) => {
      const backendFormat = this.mentionMappings[match];
      return backendFormat || match;
    });

    // Remove consecutive '@' or '#' characters, leaving only a single one
    transformedBody = transformedBody.replace(/@{2,}/g, '@').replace(/#{2,}/g, '#');
    return transformedBody;
  }

  /**
   * Format message with HTML for mentions and hashtags
   */
  formatMessage(body: string): string {
    if (!body) {
      return '';
    }

    // First decode HTML entities that might be in the content
    const decodedBody = this.decodeHtmlEntities(body);

    return decodedBody
      .replace(/@(?:team|user|everyone)_\w+_\d+|@Everyone/g, (match: string) => {
        // Case when its a user/team and everyone mention using the '@' character.
        const isTeam = match.includes('@team');
        const isEveryone = match.toLowerCase().includes('@everyone');

        // Get the mention text and remove @
        const mentionText = isEveryone ? 'Everyone' : match.split('_')[1];
        return `
                <span class="font-medium ${isTeam ? 'text-indigo-600' : 'text-blue-600'}">
                  <strong>@${mentionText}</strong>
                </span>
              `;
      })
      .replace(/\B#\w+/g, (match: string) => {
        // Case when its an associated contact using the '#' character.
        return `
                <a
                  href="/contacts/${match.split('_')?.pop()}"
                  class="pointer font-medium text-orange-600 hover:underline"
                  target="_blank"
                >
                  ${match.split('_').shift()}
                </a>
              `;
      });
  }

  /**
   * Decode HTML entities in text
   */
  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Used to sanitize the comment body by removing concatenated IDs from mentions so we have a friendly mention display on edit.
   * @param body comment body property.
   * @returns any mention present in the comment body without concatenated IDs and user/team prefixes.
   */
  sanitizeCommentBody(body: string): string {
    return body
      .replace(/@(?:user|team)_(\w+)_\d+\b/g, '@$1') // Remove user/team prefix and ID
      .replace(/[_]\d+\b/g, ''); // Remove any remaining IDs (for hashtags)
  }
}
